import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileText,
  CheckCircle2,
  Clock,
  Target,
  Building2,
  User,
  Calendar,
  Loader2,
  Send,
  AlertTriangle,
  Trophy,
  PartyPopper,
  ArrowLeft,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FinalReportPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [canSubmit, setCanSubmit] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [form, setForm] = useState({
    summary: '',
    objectives_achieved: '',
    challenges: '',
    recommendations: '',
    next_steps: ''
  });
  const [files, setFiles] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const MAX_FILES = 8;


  useEffect(() => {
    fetchData();
  }, [assignmentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get assignment details
      const assignmentRes = await axios.get(`${API}/assignments/${assignmentId}`);
      setAssignment(assignmentRes.data);
      
      // For consultants, check if they can submit
      if (user?.role === 'consultant') {
        try {
          const canSubmitRes = await axios.get(`${API}/assignments/${assignmentId}/can-submit-report`);
          setCanSubmit(canSubmitRes.data);
          
          // If already submitted, get the report
          if (canSubmitRes.data.reason === 'already_submitted') {
            const reportRes = await axios.get(`${API}/final-reports/assignment/${assignmentId}`);
            setExistingReport(reportRes.data);
          }
        } catch (err) {
          console.error('Error checking submit status:', err);
        }
      } else {
        // For admin/SME/controleur, try to get existing report
        try {
          const reportRes = await axios.get(`${API}/final-reports/assignment/${assignmentId}`);
          setExistingReport(reportRes.data);
        } catch (err) {
          // No report exists yet
          console.log('No final report exists for this assignment');
        }
      }
      
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const triggerConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.summary.trim()) {
      toast.error('Veuillez remplir le résumé de la mission');
      return;
    }
    if (!form.objectives_achieved.trim()) {
      toast.error('Veuillez décrire les objectifs atteints');
      return;
    }
    if (!files.length) {
      toast.error('Veuillez joindre au moins un fichier');
      return;
    }
    if (files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} fichiers`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/final-reports`, {
        assignment_id: assignmentId,
        ...form
      });
      // Attach each file to the freshly-created report.
      if (res.data?.id) {
        let failed = 0;
        for (const f of files) {
          try { await uploadAttachment(res.data.id, f); } catch (e) { failed += 1; }
        }
        if (failed) toast.error(`${failed} fichier(s) n'ont pas pu être envoyés. Réessayez depuis la page du rapport.`);
      }

      setShowSuccess(true);
      triggerConfetti();
      toast.success('🎉 Félicitations ! Mission accomplie !');

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadAttachment = async (reportId, fileObj) => {
    const fd = new FormData();
    fd.append('file', fileObj);
    await axios.post(`${API}/final-reports/${reportId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  // Attach file(s) to an already-submitted report (up to MAX_FILES total).
  const attachToExisting = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length || !existingReport?.id) return;
    setAttaching(true);
    try {
      for (const f of picked) {
        await uploadAttachment(existingReport.id, f);
      }
      toast.success('Fichier(s) ajouté(s)');
      const r = await axios.get(`${API}/final-reports/assignment/${assignmentId}`);
      setExistingReport(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setAttaching(false);
    }
  };

  const downloadAttachment = async (reportId, att) => {
    try {
      const res = await axios.get(`${API}/final-reports/${reportId}/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'fichier';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Impossible de télécharger le fichier'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  // Show existing report
  if (existingReport) {
    return (
      <Layout>
        <div className="space-y-6 fade-in max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>

          <Card className="border-emerald-200 bg-emerald-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-emerald-800">
                <Trophy className="w-6 h-6" />
                Mission Accomplie !
              </CardTitle>
              <CardDescription className="text-emerald-600">
                Rapport final soumis le {formatDate(existingReport.submitted_at)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-white rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-700">{existingReport.total_hours}h</p>
                  <p className="text-sm text-slate-500">Heures validées</p>
                </div>
                <div className="p-4 bg-white rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-700">{existingReport.validated_interventions}</p>
                  <p className="text-sm text-slate-500">Interventions validées</p>
                </div>
                <div className="p-4 bg-white rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-700">{existingReport.total_interventions}</p>
                  <p className="text-sm text-slate-500">Total interventions</p>
                </div>
              </div>

              {/* Report Content */}
              <div className="space-y-4 bg-white p-6 rounded-lg">
                <div>
                  <Label className="text-slate-500 text-xs uppercase">Résumé de la mission</Label>
                  <p className="mt-1 text-slate-800 whitespace-pre-wrap">{existingReport.summary}</p>
                </div>
                
                <div>
                  <Label className="text-slate-500 text-xs uppercase">Objectifs atteints</Label>
                  <p className="mt-1 text-slate-800 whitespace-pre-wrap">{existingReport.objectives_achieved}</p>
                </div>
                
                {existingReport.challenges && (
                  <div>
                    <Label className="text-slate-500 text-xs uppercase">Difficultés rencontrées</Label>
                    <p className="mt-1 text-slate-800 whitespace-pre-wrap">{existingReport.challenges}</p>
                  </div>
                )}
                
                {existingReport.recommendations && (
                  <div>
                    <Label className="text-slate-500 text-xs uppercase">Recommandations</Label>
                    <p className="mt-1 text-slate-800 whitespace-pre-wrap">{existingReport.recommendations}</p>
                  </div>
                )}
                
                {existingReport.next_steps && (
                  <div>
                    <Label className="text-slate-500 text-xs uppercase">Prochaines étapes suggérées</Label>
                    <p className="mt-1 text-slate-800 whitespace-pre-wrap">{existingReport.next_steps}</p>
                  </div>
                )}
              </div>

              {/* Final report files */}
              <div className="bg-white p-6 rounded-lg">
                <Label className="text-slate-500 text-xs uppercase">
                  Fichiers joints ({existingReport.attachments?.length || 0}/{MAX_FILES})
                </Label>
                {existingReport.attachments?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {existingReport.attachments.map((att) => (
                      <button
                        key={att.id}
                        type="button"
                        onClick={() => downloadAttachment(existingReport.id, att)}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        <FileText className="w-4 h-4" /> {att.filename} ({Math.round((att.size || 0) / 1024)} Ko)
                      </button>
                    ))}
                  </div>
                )}
                {user?.role === 'consultant' && (existingReport.attachments?.length || 0) < MAX_FILES && (
                  <div className="mt-3">
                    <p className="text-sm text-slate-600 mb-2">Ajouter un ou plusieurs fichiers (max {MAX_FILES} au total, 25 Mo chacun) :</p>
                    <Input type="file" multiple onChange={attachToExisting} disabled={attaching} />
                    {attaching && <p className="text-xs text-slate-500 mt-1">Envoi en cours…</p>}
                  </div>
                )}
                {(existingReport.attachments?.length || 0) === 0 && user?.role !== 'consultant' && (
                  <p className="mt-1 text-sm text-slate-400">Aucun fichier joint.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {/* Assignment Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Rapport Final de Mission
            </CardTitle>
            <CardDescription>
              {assignment?.sme_company_name} - {assignment?.project_name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase">Entreprise</p>
                <p className="font-medium text-slate-800 flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {assignment?.sme_company_name}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase">Projet</p>
                <p className="font-medium text-slate-800">{assignment?.project_name}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase">Heures validées</p>
                <p className="font-medium text-emerald-600">{canSubmit?.validated_hours || 0}h / {canSubmit?.min_hours || 0}h</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase">Interventions validées</p>
                <p className="font-medium text-emerald-600">{canSubmit?.validated_interventions || 0} / {canSubmit?.min_interventions || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Can't submit warning */}
        {user?.role === 'consultant' && !canSubmit?.can_submit && canSubmit?.reason === 'objectives_not_met' && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Objectifs non atteints</h3>
                  <p className="text-amber-700 mt-1">
                    Vous ne pouvez pas encore soumettre le rapport final. 
                    Les objectifs minimums suivants doivent être atteints :
                  </p>
                  <ul className="mt-2 space-y-1">
                    {canSubmit?.missing?.map((m, i) => (
                      <li key={i} className="text-amber-800 font-medium">• {m}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Bar for objectives */}
        {user?.role === 'consultant' && canSubmit && !canSubmit.can_submit && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-700">
                <Target className="w-5 h-5" />
                Progression vers le rapport final
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Hours progress */}
              <div>
                <div className="flex justify-between mb-1 text-sm">
                  <span className="text-slate-600">Heures validées</span>
                  <span className="font-medium">{canSubmit.validated_hours}h / {canSubmit.min_hours}h</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all" 
                    style={{ width: `${Math.min((canSubmit.validated_hours / canSubmit.min_hours) * 100, 100)}%` }}
                  />
                </div>
              </div>
              {/* Interventions progress */}
              <div>
                <div className="flex justify-between mb-1 text-sm">
                  <span className="text-slate-600">Interventions validées</span>
                  <span className="font-medium">{canSubmit.validated_interventions} / {canSubmit.min_interventions}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-emerald-600 h-3 rounded-full transition-all" 
                    style={{ width: `${Math.min((canSubmit.validated_interventions / canSubmit.min_interventions) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Message for non-consultants when no report exists */}
        {user?.role !== 'consultant' && !existingReport && (
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-700 text-lg">Rapport non encore soumis</h3>
              <p className="text-slate-500 mt-2">
                Le consultant n&apos;a pas encore soumis de rapport final pour cette mission.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Form - Only for consultants who can submit */}
        {user?.role === 'consultant' && canSubmit?.can_submit && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
                Objectifs atteints - Soumettez votre rapport
              </CardTitle>
              <CardDescription>
                Félicitations ! Vous avez atteint les objectifs de votre mission. 
                Complétez le rapport final ci-dessous.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Summary */}
                <div className="space-y-2">
                  <Label htmlFor="summary">Résumé de la mission *</Label>
                  <Textarea
                    id="summary"
                    placeholder="Décrivez brièvement les activités réalisées durant cette mission..."
                    value={form.summary}
                    onChange={(e) => setForm({...form, summary: e.target.value})}
                    rows={4}
                    className="resize-none"
                    required
                  />
                </div>

                {/* Objectives */}
                <div className="space-y-2">
                  <Label htmlFor="objectives">Objectifs atteints *</Label>
                  <Textarea
                    id="objectives"
                    placeholder="Listez les objectifs atteints et les résultats obtenus..."
                    value={form.objectives_achieved}
                    onChange={(e) => setForm({...form, objectives_achieved: e.target.value})}
                    rows={4}
                    className="resize-none"
                    required
                  />
                </div>

                {/* Challenges */}
                <div className="space-y-2">
                  <Label htmlFor="challenges">Difficultés rencontrées (optionnel)</Label>
                  <Textarea
                    id="challenges"
                    placeholder="Décrivez les obstacles ou défis rencontrés..."
                    value={form.challenges}
                    onChange={(e) => setForm({...form, challenges: e.target.value})}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {/* Recommendations */}
                <div className="space-y-2">
                  <Label htmlFor="recommendations">Recommandations (optionnel)</Label>
                  <Textarea
                    id="recommendations"
                    placeholder="Vos recommandations pour l'entreprise..."
                    value={form.recommendations}
                    onChange={(e) => setForm({...form, recommendations: e.target.value})}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {/* Next Steps */}
                <div className="space-y-2">
                  <Label htmlFor="next_steps">Prochaines étapes suggérées (optionnel)</Label>
                  <Textarea
                    id="next_steps"
                    placeholder="Actions recommandées pour la suite..."
                    value={form.next_steps}
                    onChange={(e) => setForm({...form, next_steps: e.target.value})}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {/* Final report files */}
                <div className="space-y-2">
                  <Label htmlFor="attachment">Fichiers du rapport final *</Label>
                  <Input
                    id="attachment"
                    type="file"
                    multiple
                    onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, MAX_FILES))}
                  />
                  {files.length > 0 && (
                    <ul className="space-y-1">
                      {files.map((f, i) => (
                        <li key={i} className="text-xs text-slate-500 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {f.name} ({Math.round((f.size || 0) / 1024)} Ko)
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-slate-400">Joignez jusqu'à {MAX_FILES} fichiers (25 Mo chacun).</p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Soumission...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Soumettre le rapport final
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Success Dialog */}
        <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
          <DialogContent className="sm:max-w-md text-center">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl flex items-center justify-center gap-2">
                <PartyPopper className="w-8 h-8 text-amber-500" />
                Félicitations !
                <Trophy className="w-8 h-8 text-amber-500" />
              </DialogTitle>
            </DialogHeader>
            <div className="py-6 space-y-4">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Mission Accomplie !</h3>
              <p className="text-slate-600">
                Votre rapport final a été soumis avec succès. 
                L&apos;administrateur et l&apos;entreprise ont été notifiés.
              </p>
              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-emerald-800 font-medium">
                  {assignment?.sme_company_name}
                </p>
                <p className="text-sm text-emerald-600">
                  {canSubmit?.validated_hours}h - {canSubmit?.validated_interventions} interventions
                </p>
              </div>
            </div>
            <DialogFooter className="justify-center">
              <Button 
                onClick={() => navigate('/consultant')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Retour au tableau de bord
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
